import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import CallOutlined from "@mui/icons-material/CallOutlined";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import ToggleOnOutlined from "@mui/icons-material/ToggleOnOutlined";
import { designColors, designShell, designTheme, designType, paginationFooter, rowsPerPageOptions } from "services/constants/designTokens";
import { GlobalConfirmDialog, GlobalBreadcrumb, sharedStickyCustomFilterBarSx, sharedStickyFooterBarSx } from "components/DesignSystem/sharedUiPatterns";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { FilterProvider } from "pages/FilterContext";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { contactInitials } from "pages/Administration/SupportContacts/supportContactsData";
import { balancedColumnWidth, monitoringBodyCellSx, monitoringHeadCellSx, monitoringNameSx, monitoringRowSx, useMeasuredHeight, useMonitoringListAccess } from "pages/Monitoring/listUi";
import { showToast } from "services/utils/Status";
import { contactToDraft } from "./contactRules";
import { contactUrl, contactPayload, readContacts } from "./contactApi";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import ContactFormPanel from "./ContactFormPanel";
import ContactDetailPanel from "./ContactDetailPanel";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";

const contactHeaders = [
  { name: "Name", key: "name", type: "string", icon: <PersonOutlineRounded fontSize="small" /> },
  { name: "Role", key: "role", type: "autocomplete", icon: <BadgeOutlined fontSize="small" /> },
  { name: "Email", key: "email", type: "string", icon: <MailOutlineRounded fontSize="small" /> },
  { name: "Phone", key: "phone", type: "string", icon: <CallOutlined fontSize="small" /> },
  { name: "Status", key: "status", type: "autocomplete", icon: <ToggleOnOutlined fontSize="small" /> },
];
const columnWidths = { role: "16%", email: "24%", phone: "19%", status: "12%" };

function CauverContact({ permissions }) {
  // Edit permission: add and update. Approver permission: delete.
  const canEdit = permissions?.canEdit !== false;
  const canDelete = permissions?.canDelete !== false;
  const history = useHistory();
  const { domain, userDetails, subModuleId, canSaveViews } = useMonitoringListAccess("Cauver Contact");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [mutationError, setMutationError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteContact, setDeleteContact] = useState(null);
  const savingRef = useRef(false);
  const mounted = useRef(false);
  const loadContacts = useCallback(async () => {
    if (!domain) throw new Error("API configuration is unavailable.");
    const result = await customGetAPI(contactUrl(domain));
    if (!result.ok) throw new Error(result.message || "Unable to load contacts.");
    const rows = readContacts(result.responseDetails);
    if (mounted.current) { setContacts(rows); setFilteredContacts(null); setLoadError(""); }
  }, [domain]);
  useEffect(() => {
    mounted.current = true;
    setLoading(true);
    loadContacts().catch((error) => { if (mounted.current) setLoadError(error.message); })
      .finally(() => { if (mounted.current) setLoading(false); });
    return () => { mounted.current = false; };
  }, [loadContacts]);
  const [filteredContacts, setFilteredContacts] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  // A single panel state keeps Add, View and Edit transitions consistent.
  const [panel, setPanel] = useState(null);
  // One expand state for the View and Add/Edit panels, so Edit opened from a
  // full-screen details view stays full screen. Collapses once they close.
  const [panelExpanded, setPanelExpanded] = useState(false);
  const togglePanelExpanded = useCallback(() => setPanelExpanded((previous) => !previous), []);
  useEffect(() => { if (!panel) setPanelExpanded(false); }, [panel]);
  const [filterRef, filterHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();
  const resetPage = useCallback(() => setPage(0), []);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain, userId: userDetails?.id, subModuleId,
    storageKey: "cauver_contacts_saved_views", fullHeaders: contactHeaders,
    screenAccess: canSaveViews, onViewChange: resetPage,
  });
  const data = useMemo(() => contacts.map((contact) => ({ ...contact, status: contact.active ? "Active" : "Inactive" })), [contacts]);
  const visibleContacts = useMemo(() => {
    if (filteredContacts === null) return data;
    const byId = new Map(data.map((contact) => [contact.id, contact]));
    return filteredContacts.map((contact) => byId.get(contact.id)).filter(Boolean);
  }, [data, filteredContacts]);
  const safePage = Math.min(page, Math.max(0, Math.ceil(visibleContacts.length / rowsPerPage) - 1));
  const selected = contacts.find((contact) => contact.id === panel?.id);
  const openContact = (id, mode = "view") => { if (!savingRef.current && (mode === "view" || canEdit)) { setMutationError(""); setPanel({ id, mode }); } };
  const mutateContact = async (draft, deleting = false) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setMutationError("");
    try {
      const result = await customAPI(contactUrl(domain), draft.id == null ? "POST" : "PUT", contactPayload(draft, deleting));
      if (!result.ok) throw new Error(result.message || "Unable to save contact.");
      if (!mounted.current) return;
      if (deleting) setContacts((previous) => previous.filter((row) => row.id !== draft.id));
      else if (draft.id != null) setContacts((previous) => previous.map((row) => row.id === draft.id ? {
        ...row, ...draft, phoneNumber: draft.phone, whatsappNumber: draft.whatsapp,
        phone: draft.phoneCode + " " + draft.phone, whatsapp: draft.whatsapp ? draft.whatsappCode + " " + draft.whatsapp : "",
      } : row));
      setDeleteContact(null);
      setPanel(deleting || draft.id == null ? null : { mode: "view", id: draft.id });
      setPage(0);
      showToast("success", deleting ? "Contact deleted." : "Contact saved.");
      try { await loadContacts(); } catch (error) { if (mounted.current) setLoadError("Contact saved, but the list could not be refreshed. " + error.message); }
    } catch (error) { if (mounted.current) setMutationError(error.message); }
    finally { savingRef.current = false; if (mounted.current) setSaving(false); }
  };
  const saveContact = (draft) => mutateContact(draft);
  const renderCell = (key, contact) => {
    if (key === "name") return (
      <Mui.Stack direction="row" spacing={1.5} alignItems="center">
        <Mui.Avatar sx={{ width: 36, height: 36, fontSize: 13, bgcolor: designColors.tealTint, color: designColors.tealDark }}>{contactInitials(contact.name)}</Mui.Avatar>
        <Mui.ButtonBase onClick={(event) => { event.stopPropagation(); openContact(contact.id); }} sx={{ minWidth: 0, textAlign: "left", borderRadius: 1 }}>
          <Mui.Typography noWrap sx={monitoringNameSx(panel?.id === contact.id)}>{contact.name}</Mui.Typography>
        </Mui.ButtonBase>
      </Mui.Stack>
    );
    if (key === "role") return <Mui.Chip size="small" label={contact.role} sx={{ bgcolor: "#DBEAFE", color: "#1D4ED8", borderRadius: 1, maxWidth: "100%" }} />;
    if (key === "status") return <RequestStatusChip status={contact.status} colorCode={contact.active ? "#0F9F6E" : "#6B7280"} />;
    return contact[key];
  };
  if (loading) return <Loader info="Loading..." />;
  return (
    <Mui.ThemeProvider theme={designTheme}>
      <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
        <Mui.Box sx={{ ...designShell.pageSurface, p: { xs: 2, md: 3 } }}>
          <Mui.Stack spacing={2}>
            <GlobalBreadcrumb trail={[{ label: "System Configuration", onClick: () => history.push("/admin/support/admin") }, { label: "Cauver Contact" }]} />
            <Mui.Box>
              <Mui.Typography component="h1" sx={designType.pageTitle}>Cauver Contact</Mui.Typography>
              <Mui.Typography sx={designType.pageSubtitle}>Manage Cauver support contacts who are visible to customer organisations.</Mui.Typography>
            </Mui.Box>
            {loadError && <Mui.Alert severity="error" action={<Mui.Button onClick={() => loadContacts().catch((error) => setLoadError(error.message))}>Retry</Mui.Button>}>{loadError}</Mui.Alert>}
            <Mui.Box ref={filterRef} sx={{ ...designShell.sectionCard, ...sharedStickyCustomFilterBarSx }}>
              <CustomFilter title="contacts" titleOverride={titleOverride} filterHeaders={visibleHeaders}
                data={data} filteredData={visibleContacts} filteredDataReady={filteredContacts !== null}
                onFilteredDataChange={setFilteredContacts} screenAccess={true} hideCount={true} labeledToolbar={true}
                downloadFormats={["excel", "pdf", "csv"]} searchPlaceholder="Search by name, role or email..."
                newButtonComponent={canEdit && <PrimaryButton startIcon={<AddRounded />} disabled={saving || Boolean(loadError)} onClick={() => { setMutationError(""); setPanel({ mode: "add" }); }}
                  sx={{ minWidth: 0, minHeight: 40, whiteSpace: "nowrap", flexShrink: 0 }}>Add Contact</PrimaryButton>} />
            </Mui.Box>
            <Mui.Box sx={{ ...designShell.sectionCard, width: "100%" }}>
              <Mui.TableContainer sx={{ width: "100%", overflowX: { xs: "auto", md: "visible" }, pb: footerHeight ? footerHeight + "px" : 0 }}>
                <ApproveTable stickyHeader aria-label="Cauver contacts" sx={{ width: "100%", minWidth: 720, tableLayout: "fixed" }}>
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                      {visibleHeaders.map((header) => <Mui.TableCell key={header.key} scope="col" sx={{ ...monitoringHeadCellSx(filterHeight), width: balancedColumnWidth(header.key, visibleHeaders, contactHeaders, columnWidths) }}>{header.name}</Mui.TableCell>)}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {!visibleContacts.length ? <TableEmptyState dataLength={contacts} colSpan={visibleHeaders.length} /> :
                      visibleContacts.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage).map((contact) => (
                        <Mui.TableRow key={contact.id} onClick={() => openContact(contact.id)} sx={monitoringRowSx(panel?.id === contact.id)}>
                          {visibleHeaders.map((header) => <Mui.TableCell key={header.key} title={contact[header.key]} sx={{ ...monitoringBodyCellSx, overflow: "hidden", textOverflow: "ellipsis" }}>{renderCell(header.key, contact)}</Mui.TableCell>)}
                        </Mui.TableRow>
                      ))}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
              <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
                {paginationFooter({ page: safePage, rowsPerPage, total: visibleContacts.length, itemLabel: "contacts", rowsPerPageOptions,
                  onPageChange: setPage, onRowsPerPageChange: (event) => { setRowsPerPage(Number(event.target.value)); setPage(0); } })}
              </Mui.Box>
            </Mui.Box>
          </Mui.Stack>
        </Mui.Box>
      </FilterProvider>
      {panel?.mode === "view" && selected && <ContactDetailPanel contact={selected} onClose={() => setPanel(null)} onEdit={() => openContact(selected.id, "edit")} onDelete={() => { setMutationError(""); setDeleteContact(selected); }} disabled={saving} canEdit={canEdit} canDelete={canDelete} expanded={panelExpanded} onToggleExpand={togglePanelExpanded} />}
      {canEdit && (panel?.mode === "add" || (panel?.mode === "edit" && selected)) &&
        <ContactFormPanel key={panel.mode + (panel.id ?? "")} contact={selected} contacts={contacts} onSave={saveContact} saving={saving} error={mutationError}
          expanded={panelExpanded} onToggleExpand={togglePanelExpanded}
          onClose={() => { if (!savingRef.current) setPanel(panel.mode === "edit" ? { mode: "view", id: panel.id } : null); }} />}
      <GlobalConfirmDialog open={Boolean(deleteContact)} title="Delete contact?" danger loading={saving} loadingLabel="Deleting..." confirmLabel="Delete" cancelLabel="Cancel"
        onCancel={() => { if (!savingRef.current) { setDeleteContact(null); setMutationError(""); } }}
        onConfirm={() => canDelete && deleteContact && mutateContact(contactToDraft(deleteContact), true)}>
        Delete {deleteContact?.name}? {mutationError && <Mui.Alert severity="error">{mutationError}</Mui.Alert>}
      </GlobalConfirmDialog>
    </Mui.ThemeProvider>
  );
}

export default withScreenPermissions(CauverContact, SCREEN_ACCESS.cauverContact, "Cauver Contact");
